import { useEffect, useMemo, useState } from "react";

import { ColumnMappingTable } from "@/components/tradeideas/column-mapping-table";
import { ScannerList } from "@/components/tradeideas/scanner-list";
import {
  TradeIdeasEmpty,
  TradeIdeasError,
  TradeIdeasPage,
} from "@/components/tradeideas/tradeideas-page";
import {
  useTradeIdeasColumnMappings,
  useTradeIdeasFilters,
  useTradeIdeasScanners,
  useUpdateTradeIdeasColumnMapping,
} from "@/hooks/tradeideas/useTradeIdeasBot";

export default function TradeIdeasScannersPage() {
  const scannersQ = useTradeIdeasScanners();
  const filtersQ = useTradeIdeasFilters();
  const scanners = useMemo(() => scannersQ.data ?? [], [scannersQ.data]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedScanner = scanners.find((scanner) => scanner.id === selectedId) ?? null;
  const mappingsQ = useTradeIdeasColumnMappings(selectedId);
  const updateMapping = useUpdateTradeIdeasColumnMapping(selectedId ?? "");

  useEffect(() => {
    if (!selectedId && scanners.length > 0) {
      setSelectedId(scanners[0].id);
    }
  }, [selectedId, scanners]);

  return (
    <TradeIdeasPage
      title="TradeIdeas Scanners"
      description="Discovered alert windows, toplists, CSV columns, and standard mappings."
    >
      {scannersQ.isError ? (
        <TradeIdeasError message={(scannersQ.error as Error).message} />
      ) : scannersQ.isLoading && scanners.length === 0 ? (
        <TradeIdeasEmpty message="Loading scanners..." />
      ) : scanners.length === 0 ? (
        <TradeIdeasEmpty message="No scanners discovered yet. Start the TI bot and let it read CSV headers." />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[320px_1fr]">
          <ScannerList
            scanners={scanners}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
          <div className="min-w-0 space-y-3">
            <div>
              <h2 className="text-base font-semibold">
                {selectedScanner?.name ?? "Scanner"}
              </h2>
              <p className="truncate text-xs text-muted-fg">
                {selectedScanner?.last_file_path ?? "No file path reported"}
              </p>
            </div>
            {mappingsQ.isError ? (
              <TradeIdeasError message={(mappingsQ.error as Error).message} />
            ) : mappingsQ.isLoading && !mappingsQ.data ? (
              <TradeIdeasEmpty message="Loading scanner columns..." />
            ) : (
              <ColumnMappingTable
                columns={mappingsQ.data ?? []}
                filters={filtersQ.data ?? []}
                pending={updateMapping.isPending}
                onSave={(columnId, patch) => updateMapping.mutate({ columnId, patch })}
              />
            )}
          </div>
        </div>
      )}
    </TradeIdeasPage>
  );
}
